/** Reusable "badge set with popover" for displaying a list of tags.
 *
 * The compact view (what renders inline, e.g. in a grid cell) shows up to
 * `maxVisible` tag chips plus a "+N" overflow chip. Clicking anywhere in the
 * compact set (except directly on a chip) opens a Popover with the FULL list
 * grouped by category — sorted by category code — like:
 *   code1: tag1, tag2
 *   code2: tag3, tag4, tag5
 *
 * Each chip is colored deterministically by its category's `code` (see
 * tagCategoryColor.ts) — the same code always gets the same color, including
 * across different tenants. Clicking an individual tag chip — in the compact
 * set OR inside the popover — calls `onToggleTag` if the caller passed one,
 * without opening/closing the popover as a side effect; this is the seam a
 * parent uses to wire "click a tag to toggle it as an active filter". Not
 * bookings-specific — tags show up in a lot of places in the console, and
 * this component is meant to be reused wherever they do.
 *
 * `chipActions` gives every chip a hover/click popover of quick actions (open
 * the tag, filter a grid by it, copy — see `tagChipActions`). It composes with
 * `onToggleTag`: the chip's own click still toggles (and stops propagating),
 * so a grid keeps click = toggle while hover reveals the rest.
 */
import { Box, Chip, Popover, Stack, Typography } from '@mui/material';
import { useState } from 'react';

import { ValueActions, type ValueAction } from '@/components/ValueActions/ValueActions';
import { categoryColor } from '@/utils/tagCategoryColor';

export interface TagBadge {
  id: number;
  name: string;
  /** `id` is optional because some payloads carry the category by code only;
   *  when present it enables the "Open category" chip action. */
  category: { code: string; name?: string; id?: number } | null;
}

interface TagChipProps<T extends TagBadge> {
  tag: T;
  size: 'small' | 'medium';
  active: boolean;
  onClick?: (e: React.MouseEvent) => void;
  actions?: ValueAction[];
}

function TagChip<T extends TagBadge>({ tag, size, active, onClick, actions }: TagChipProps<T>) {
  const { bg, fg } = categoryColor(tag.category?.code);
  const chip = (
    <Chip
      size={size}
      label={tag.name}
      onClick={onClick}
      clickable={Boolean(onClick)}
      sx={{
        bgcolor: bg,
        color: fg,
        fontWeight: active ? 700 : 400,
        border: active ? `1.5px solid ${fg}` : '1.5px solid transparent',
        height: 20,
        fontSize: 12,
        '& .MuiChip-label': { px: 0.75 },
      }}
    />
  );
  return actions?.length ? (
    <ValueActions chip actions={actions}>
      {chip}
    </ValueActions>
  ) : (
    chip
  );
}

function groupByCategory<T extends TagBadge>(
  tags: T[],
): Array<{ code: string; label: string; tags: T[] }> {
  const groups = new Map<string, { code: string; label: string; tags: T[] }>();
  for (const tag of tags) {
    const code = tag.category?.code ?? '';
    if (!groups.has(code)) {
      groups.set(code, { code, label: tag.category?.name || code || 'Uncategorized', tags: [] });
    }
    groups.get(code)!.tags.push(tag);
  }
  return [...groups.values()].sort((a, b) => a.code.localeCompare(b.code));
}

export interface TagBadgeSetProps<T extends TagBadge> {
  tags: T[];
  /** Called when a tag chip is clicked, in the compact set or the popover.
   *  Omit to render plain, non-interactive chips (no filter wiring). */
  onToggleTag?: (tag: T) => void;
  /** Whether a given tag should render as "active" (e.g. currently applied
   *  as a filter elsewhere) — purely visual; this component holds no
   *  filter state of its own. */
  isTagActive?: (tag: T) => boolean;
  /** Per-chip quick actions (hover/click popover). Usually `tagChipActions`. */
  chipActions?: (tag: T) => ValueAction[];
  /** How many chips show before collapsing into "+N". Default 3. */
  maxVisible?: number;
  size?: 'small' | 'medium';
  emptyText?: string;
}

export function TagBadgeSet<T extends TagBadge>({
  tags,
  onToggleTag,
  isTagActive,
  chipActions,
  maxVisible = 3,
  size = 'small',
  emptyText = '—',
}: TagBadgeSetProps<T>) {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  // Defensive: the prop is typed non-optional, but a rolling deploy can pair a
  // newer console with a backend whose payload has no `tags` key yet, and a
  // missing key must not take the whole grid down with it.
  const items = tags ?? [];
  if (items.length === 0) return <>{emptyText}</>;

  const visible = items.slice(0, maxVisible);
  const hiddenCount = items.length - visible.length;
  const groups = groupByCategory(items);

  const chipClick = (tag: T) => (e: React.MouseEvent) => {
    e.stopPropagation();
    onToggleTag?.(tag);
  };

  return (
    <>
      <Box
        onClick={(e) => {
          e.stopPropagation();
          setAnchorEl(e.currentTarget);
        }}
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 0.5,
          alignItems: 'center',
          cursor: 'pointer',
          py: 0.5,
          minHeight: 32,
        }}
      >
        {visible.map((tag) => (
          <TagChip
            key={tag.id}
            tag={tag}
            size={size}
            active={isTagActive?.(tag) ?? false}
            onClick={onToggleTag ? chipClick(tag) : undefined}
            actions={chipActions?.(tag)}
          />
        ))}
        {hiddenCount > 0 && (
          <Chip size={size} label={`+${hiddenCount}`} sx={{ bgcolor: 'action.hover' }} />
        )}
      </Box>

      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        onClick={(e) => e.stopPropagation()}
      >
        <Box sx={{ p: 1, maxWidth: 380 }}>
          <Stack spacing={0.5}>
            {groups.map((group) => (
              <Box
                key={group.code}
                sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 0.5 }}
              >
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ fontSize: 11, whiteSpace: 'nowrap' }}
                >
                  {group.label}:
                </Typography>
                {group.tags.map((tag) => (
                  <TagChip
                    key={tag.id}
                    tag={tag}
                    size={size}
                    active={isTagActive?.(tag) ?? false}
                    onClick={onToggleTag ? chipClick(tag) : undefined}
                    actions={chipActions?.(tag)}
                  />
                ))}
              </Box>
            ))}
          </Stack>
        </Box>
      </Popover>
    </>
  );
}
